import React from 'react';
import * as R from 'ramda';
import styled from 'styled-components';
import {
  left,
  space,
  width,
  zIndex,
  height,
  system,
  bottom,
  overflow,
  fontSize,
  minWidth,
  minHeight,
  borderRadius,
} from 'styled-system';
// helpers/constants
import * as G from '../helpers';
import * as GC from '../constants';
// ui
import { Box } from '../ui';
// forms
import { renderBorderColor } from './helpers';
//////////////////////////////////////////////////

const isInfoPresent = (info) => G.isAllTrue(
  G.isNotNilAndNotEmpty(info),
  G.notEquals(info, GC.THREE_DOTS_STRING),
);

export const Form = styled.form`
  ${space}
  ${width}
  ${height}
  ${overflow}
  ${minHeight}

  display: flex;
  position: relative;

  z-index: ${({ zIndex }) => zIndex || 12};
  align-items: ${({ alignItems }) => alignItems};
  flex-direction: ${({ direction }) => direction || 'column'};
`;

Form.defaultProps = {
  minHeight: 'calc(100% - 60px)',
};

export const PureStyledForm = styled.form`
  ${space}
  ${width}
  ${height}
  ${overflow}
  ${minWidth}
  ${minHeight}
`;

export const LeftLabelsWrapper = styled.aside`
  ${left}
  ${width}

  z-index: -1;
  min-height: 100%;
  position: absolute;
  transition: left .2s linear;
  background-color: ${() => G.getTheme('forms.labelsWrapper.bgColor')};
`;

LeftLabelsWrapper.defaultProps = {
  width: 320,
  left: '1px',
};

export const FormGroupWrapper = styled.section`
  ${space}

  width: 100%;

  z-index: ${({ zi }) => zi};
  display: ${({ display }) => display};
  position: ${({ position }) => position || 'static'};
  flex-direction: ${({ flexDirection }) => flexDirection};
  height: ${({ isOpened }) => G.ifElse(
    R.and(R.equals(isOpened, false), G.isNotNil(isOpened)), '32px', 'auto')
  };
  overflow: ${({ isOpened }) => G.ifElse(
    R.and(R.equals(isOpened, false), G.isNotNil(isOpened)), 'hidden', 'visible')
  };
`;

export const FormGroup = styled.div`
  ${space}
  ${width}

  z-index: ${({ zI }) => zI || 'unset'};
  position: ${({ position }) => position};
  display: ${({ display }) => display || 'flex'};
  align-items: ${({ align }) => align || 'center'};
  justify-content: ${({ jc }) => jc || 'baseline'};
  min-height: ${({ minHeight }) => minHeight || '30px'};
  flex-direction: ${({ direction }) => direction || 'column'};

  ${({ additionClass }) => additionClass}
`;

export const FormGroupTitle = styled.div`
  ${space}

  height: 30px;
  line-height: 30px;
  padding-left: 15px;

  background-color: ${() => G.getTheme('colors.mainBlue')};
  color: ${() => G.getTheme('forms.group.title.textColor')};
`;

export const FormGroupTitleWrapper = styled.div`
  position: relative;

  z-index: ${({ zIndex }) => zIndex};

  & .arrow {
    right: 16px;
    top: 11px;
    position: absolute;
    transform: ${({isOpened}) => isOpened && 'rotate(180deg)'};
  }
`;

export const TabFormWrapper = styled.div`
  background: linear-gradient(
    to right,
    ${() => G.getTheme('labelsWrapper.bgColor')} 320px,
    ${() => G.getTheme('pages.layOutBgColor')} 0%
  );
  & > form > section {
    z-index: unset;
  }
`;

export const InputWrapper = styled.span`
  ${space}
  ${width}

  display: flex;
  position: relative;
  align-items: center;
`;

export const Input = styled.input`
  ${space}
  ${width}
  ${height}
  ${fontSize}

  z-index: 2;
  cursor: text;
  outline: none;

  padding: ${({ p }) => p || '0 8px'};
  height: ${({ height }) => height || '30px'};
  text-align: ${({ p }) => R.equals(p, '0') && 'center'};
  line-height: ${({ lineHeight }) => lineHeight || '30px'};
  border-radius: ${({ borderRadius }) => borderRadius || '2px'};
  border: ${(props) => G.ifElse(props.withoutBorder, 'none', '1px solid')};
  border-color: ${(props) => G.ifElse(
    G.isNotNilAndNotEmpty(props.borderColor),
    props.borderColor,
    renderBorderColor(props),
  )};
  background-color: ${({ bgColor, disabled }) => (
    bgColor ||
    G.getTheme(G.ifElse(disabled, 'forms.inputs.bgDisabled', 'forms.inputs.bgColor'))
  )};

  &:focus {
    box-shadow: ${({ withoutBorder }) => R.not(withoutBorder) && '0 0 5px 0 rgba(206, 40, 40, 0.5)'};
    box-shadow: ${({ version, withoutBorder }) => (
      R.not(withoutBorder) &&
      `0 0 5px 0 ${G.getThemeByCond(G.isThemeSecondVersion(version), 'colors.boxShadowBlue', 'colors.boxShadowRed')}`
    )};
  }

  &[type=number]::-webkit-inner-spin-button,
  &[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    margin: 0;
  }

  & + .MuiInputAdornment-root {
    min-width: 31px;
    margin-left: 2px;
  }
`;

export const Textarea = styled.textarea`
  ${space}
  ${width}
  ${height}

  cursor: text;
  resize: none;
  outline: none;
  font-size: 16px;
  line-height: 24px;
  padding: 5px 20px;

  border: 1px solid ${(props) => renderBorderColor(props)};
  border-radius: ${({ borderRadius }) => borderRadius || '1px'};
  background-color: ${({ disabled }) =>
    G.getThemeByCond(disabled, 'forms.inputs.bgDisabled', 'forms.inputs.bgColor')};

  &:focus {
    box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
  }
`;

Textarea.defaultProps = {
  height: 72,
  width: 540,
};

const afterPosition = system({
  afterTop: {
    property: 'top',
    transform: (value) => G.ifElse(G.isNumber(value), `${value}px`, value),
  },
  afterRight: {
    property: 'right',
    transform: (value) => G.ifElse(G.isNumber(value), `${value}px`, value),
  },
});

export const SelectWrapper = styled.div`
  ${width}

  position: relative;

  &:after {
    width: 6px;
    height: 6px;
    content: '';
    z-index: 10;
    position: absolute;
    border: solid black;
    pointer-events: none;
    border-width: 0px 1px 1px 0;
    transform: rotate(45deg) translate(0, -60%);

    ${afterPosition}
  }
`;

SelectWrapper.defaultProps = {
  afterTop: 18,
  afterRight: 25,
};

export const SelectComponent = styled.select`
  ${space}
  ${width}
  ${fontSize}
  ${borderRadius}

  z-index: 0;
  height: 30px;
  outline: none;
  appearance: none;
  line-height: 30px;
  position: relative;

  background-color: ${() => G.getTheme('forms.inputs.bgColor')};
  border: 1px solid ${(props) => renderBorderColor(props)};

  &:focus {
    box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
  }
`;

SelectComponent.defaultProps = {
  pr: 25,
  pl: '8px',
  borderRadius: '2px',
};

const Option = styled.option``;

export const Checkbox = styled.input`
  ${space}

  z-index: 0;
  position: relative;
  margin-bottom: ${({ mb }) => mb};

  &:focus {
    outline: none;
  }

  &:before {
    left: 0;
    top: 50%;
    content: '';
    width: 16px;
    height: 16px;
    border-radius: 2px;
    position: absolute;
    transform: translate(0, -50%);

    background-color: ${() => G.getTheme('forms.checkboxes.bgColor')};
    border: 1px solid ${({ borderColor }) => G.getTheme(G.ifElse(
      G.isNotNil(borderColor),
      borderColor,
      'forms.checkboxes.borderColor',
    ))};
  }

  &:checked:before {
    border: 1px solid ${({ borderColorChecked }) => G.getTheme(G.ifElse(
      G.isNotNil(borderColorChecked),
      borderColorChecked,
      'forms.checkboxes.borderColorChecked',
    ))};
    content: '';
    width: 16px;
    height: 16px;
    border-radius: 2px;
    position: absolute;
    background-color: ${() => G.getTheme('forms.checkboxes.bgColorChecked')};
  }

  &:checked:after {
    border: 1px solid ${({ textColorChecked }) => G.getTheme(G.ifElse(
      G.isNotNil(textColorChecked),
      textColorChecked,
      'forms.checkboxes.textColorChecked',
    ))};
    left: 0;
    top: 50%;
    width: 3px;
    height: 8px;
    content: '';
    position: absolute;
    border-width: 0px 2px 2px 0;
    transform: rotate(45deg) translate(0, -90%);
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled:before,
  &:disabled:checked:before {
    border: 1px solid ${() => G.getTheme('colors.light.darkGrey')};
    background-color: ${() => G.getTheme('colors.lightGrey')};
  }

  &:disabled:checked:after {
    border-color: ${() => G.getTheme('colors.darkGrey')};
  }
`;

export const Label = styled.label`
  ${space}
  ${width}
  ${fontSize}

  display: flex;

  cursor: ${({ cursor }) => cursor};
  min-width: ${({ minWidth }) => minWidth};
  font-weight: ${({ fontWeight }) => fontWeight};
  flex-direction: ${({ flexDirection }) => flexDirection};
  color: ${({ color }) => color || G.getTheme('forms.inputs.labelColor')};

  &.required::after {
    content: '*';
    color: ${() => G.getTheme('colors.light.mainRed')};
  }

  ${({ fieldClass }) => G.ifElse(G.isObject(fieldClass), fieldClass)}
`;

export const Error = styled.span`
  ${space}
  ${fontSize}

  top: ${({ errorTop }) => errorTop};
  left: ${({ errorLeft }) => errorLeft};
  z-index: ${({ zIndex }) => zIndex || 'unset'};
  display: ${({ errorDisplay }) => errorDisplay};
  cursor: ${({ cursor }) => R.or(cursor, 'auto')};
  position: ${({ errorPosition }) => errorPosition};
  color: ${() => G.getTheme('forms.inputs.errorTextColor')};
  overflow: ${({ errorOverflow }) => errorOverflow};
  text-align: ${({ errorTextAlign }) => errorTextAlign};
  white-space: ${({ errorWhiteSpace }) => errorWhiteSpace};
  width: ${({ errorWidth }) => errorWidth || 'max-content'};
  text-overflow: ${({ errorTextOverflow }) => errorTextOverflow};

  ${({ errorClass }) => errorClass}
`;

export const ErrorsWrapper = styled.span`
  ${space}
  ${fontSize}

  display: flex;
  padding: 10px 20px;
  flex-direction: column;

  color: ${() => G.getTheme('forms.inputs.errorTextColor')};

  & span + span {
    margin: 5px 0 0;
  }
`;

const InfoWrapper = styled.div`
  ${space}
  ${bottom}
  ${zIndex}

  position: relative;
  align-items: center;
  display: inline-flex;

  & .info-icon {
    ${width}
    ${height}
    ${minWidth}

    cursor: help;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    border-radius: 50%;
    color: ${() => G.getTheme('colors.mainBlue')};
    border: 1px solid ${() => G.getTheme('colors.mainBlue')};
    &:hover {
      color: ${() => G.getTheme('info.hoverIconColor')};
      background-color: ${() => G.getTheme('colors.mainBlue')};
    }
  }
  & .info-text {
    left: 16px;
    padding: 5px;
    display: none;
    margin: 0 5px;
    font-size: 12px;
    max-width: 400px;
    position: absolute;
    width: max-content;
    border-radius: 2px;
    word-break: break-word;
    background-color: ${() => G.getTheme('info.bgColor')};
    box-shadow: 0 0 2px 2px ${() => G.getTheme('colors.mainBlue')};
  }
  & .info-icon:hover + .info-text {
    display: inline-flex;
  }
`;

InfoWrapper.defaultProps = {
  width: 16,
  zIndex: 11,
  height: 16,
  minWidth: 16,
};

export const MultiSelectWrapper = styled.div`
  ${space}
  ${width}

  z-index: ${({ zIndex }) => zIndex || '10'};
  & > div.Select {
    & .Select-control {
      height: 30px;
      border-radius: 2px;
      border-color: ${(props) => renderBorderColor(props)};
      & .Select-placeholder {
        line-height: 30px;
      }
      & .Select-value {
        width: 100%;
        line-height: 30px;
        & .Select-value-label {
          width: calc(100% - 20px);
        }
      }
    }
    &.is-focused:not(.is-open) .Select-control {
      box-shadow: 0 0 5px 0 rgba(206, 40, 40, 0.5);
      border-color: ${() => G.getTheme('forms.inputs.borderColor')};
    }
  }
  & .Select-input {
    height: 28px;
    padding: 0px;
    & input {
      height: 28px;
      padding: 0px;
    }
  }
`;

export const ToggleWrapper = styled.section`
  ${space}
  ${width}

  display: flex;
`;

export const Info = (props) => {
  const { text } = props;

  return (
    <InfoWrapper {...props}>
      <Box cursor='help' className='info-icon'>i</Box>
      {text && <Box className='info-text'>{text}</Box>}
    </InfoWrapper>
  );
};

export const CleanWrapper = styled.div`
  top: 0px;
  right: 15px;
  width: 23px;
  z-index: 14;
  height: 30px;
  padding: 7px 5px;
  position: absolute;
`;

export const renderOptions = (options) => {
  if (R.isNil(options)) return;

  if (R.not(Array.isArray(options))) {
    const defaultOptions = [<Option key={1} value='' />];
    R.forEachObjIndexed((opt) => (
      defaultOptions.push(<Option key={opt.guid} value={opt.guid}>{opt.displayValue}</Option>)
    ), options);

    return defaultOptions;
  }

  return options.map((option, index) => {
    if (R.is(String, option)) {
      return <Option key={index} value={option}>{option}</Option>;
    }

    return (
      <Option key={index} disabled={option.disabled} value={R.or(option.value, option.guid)}>
        {R.or(option.name, option.label)}
      </Option>
    );
  });
};
